import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useT, useLanguage } from '../i18n';
import { PhoneField, Country, DEFAULT_COUNTRY, joinPhone } from '../components/PhoneField';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const API_BASE_URL = '/api';

async function postJson(path: string, body: unknown) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP ${response.status}`);
  }
  return data;
}

export function SignupPage() {
  const t = useT();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [trialDays, setTrialDays] = useState(14);

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const { language } = useLanguage();
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phoneNumber, setPhoneNumber] = useState('');
  const phone = joinPhone(country, phoneNumber);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/auth/signup/status`)
      .then(response => response.json())
      .then(data => { setEnabled(!!data.enabled); setTrialDays(data.trialDays || 14); })
      .catch(() => setEnabled(false))
      .finally(() => setChecking(false));
  }, []);

  // Contagem regressiva para reenviar o código
  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = window.setTimeout(() => setResendIn(value => value - 1), 1000);
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, [resendIn]);

  const requestCode = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setBusy(true);
    try {
      const data = await postJson('/auth/signup/request-code', { phone });
      setStep('code');
      setResendIn(data.resendInSeconds || 60);
      toast.success(t('signup.codeSent'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('signup.errorSend'));
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await postJson('/auth/signup/verify', { phone, code, name, companyName, password });
      toast.success(t('signup.created'));
      const signedIn = await login(phone, password);
      // Recarrega na página de conexões: o primeiro passo é ligar o WhatsApp.
      // A navegação normal corria com o carregamento da sessão e caía na tela padrão.
      if (signedIn) {
        window.location.assign('/whatsapp');
      } else {
        navigate('/login', { replace: true });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('signup.errorCreate'));
    } finally {
      setBusy(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="mb-8">
          <div className="flex justify-end">
            <LanguageSwitcher className="text-gray-500 hover:text-gray-800 hover:bg-gray-100" />
          </div>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">{t('signup.title')}</h1>
            <p className="text-gray-600 mt-2">
              {checking ? t('common.loading') : enabled ? t('signup.trialSubtitle', { days: trialDays }) : t('signup.closed')}
            </p>
          </div>
        </div>

        {!checking && !enabled && (
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-500">{t('signup.closedText')}</p>
            <Link to="/login" className="inline-block text-emerald-700 font-medium hover:text-emerald-800">
              {t('signup.goSignIn')}
            </Link>
          </div>
        )}

        {!checking && enabled && step === 'phone' && (
          <form onSubmit={requestCode} className="space-y-5">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {t('signup.phone')}
              </label>
              <PhoneField
                id="phone"
                country={country}
                number={phoneNumber}
                onCountryChange={setCountry}
                onNumberChange={setPhoneNumber}
                language={language}
                disabled={busy}
              />
              <p className="text-xs text-gray-500 mt-2">{t('signup.phoneHelp')}</p>
            </div>

            <button
              type="submit"
              disabled={busy || phoneNumber.length < 6}
              className="w-full py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? t('signup.sending') : t('signup.sendCode')}
            </button>
          </form>
        )}

        {!checking && enabled && step === 'code' && (
          <form onSubmit={createAccount} className="space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-800 flex items-center justify-between">
              <span dir="ltr">{phone}</span>
              <button type="button" onClick={() => setStep('phone')} className="text-emerald-700 underline">{t('signup.change')}</button>
            </div>

            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">{t('signup.code')}</label>
              <input
                id="code"
                inputMode="numeric"
                dir="ltr"
                maxLength={6}
                value={code}
                onChange={event => setCode(event.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className={`${inputClass} tracking-[0.5em] text-center text-lg`}
                required
                disabled={busy}
              />
              <button
                type="button"
                onClick={() => requestCode()}
                disabled={busy || resendIn > 0}
                className="mt-2 text-xs text-emerald-700 hover:text-emerald-800 disabled:text-gray-400"
              >
                {resendIn > 0 ? t('signup.resendIn', { seconds: resendIn }) : t('signup.resend')}
              </button>
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">{t('signup.name')}</label>
              <input id="name" dir="auto" value={name} onChange={event => setName(event.target.value)} className={inputClass} required disabled={busy} />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">{t('signup.company')}</label>
              <input id="company" dir="auto" value={companyName} onChange={event => setCompanyName(event.target.value)} className={inputClass} required disabled={busy} />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">{t('signup.password')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                className={inputClass}
                placeholder={t('signup.passwordPlaceholder')}
                minLength={8}
                required
                disabled={busy}
              />
              <p className="text-xs text-gray-500 mt-2">{t('signup.passwordHelp')}</p>
            </div>

            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? t('signup.creating') : t('signup.create')}
            </button>
          </form>
        )}

        {!checking && enabled && (
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('signup.haveAccount')}{' '}
            <Link to="/login" className="text-emerald-700 font-medium hover:text-emerald-800">{t('common.signIn')}</Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default SignupPage;
